import { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLoaderData, useSubmit, useLocation, useParams } from 'react-router';
import { authedApiFetch } from '../../../utils/Api';
import KanbanBoard from '../components/KanbanBoard';
import PipelineFilters from '../components/PipelineFilters';

export const loader = async ({ params, request }) => {
	const { tenantId } = params;

	// Get filters from URL if they exist
	const url = new URL(request.url);
	const owner_id = url.searchParams.get('owner_id') || '';
	const fund_id = url.searchParams.get('fund_id') || '';
	const industry = url.searchParams.get('industry') || '';

	// Add filters to request params if they have values
	const requestParams = {};
	if (owner_id) requestParams.owner_id = owner_id;
	if (fund_id) requestParams.fund_id = fund_id;
	if (industry) requestParams.industry = industry;

	try {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/pipeline`,
			method: 'GET',
			params: requestParams,
		});
		return response;
	} catch (error) {
		console.error('Error loading pipeline data:', error);
		return { pipeline: [], filters: null };
	}
};

/**
 * Pipeline page for the Investing app
 * Displays deal pipeline and opportunities in various stages
 */
const PipelinePage = () => {
	const theme = useTheme();
	const pipelineData = useLoaderData();
	const location = useLocation();
	const submit = useSubmit();
	const { tenantId } = useParams();

	// Get filter values from URL search params
	const searchParams = new URLSearchParams(location.search);
	const initialFilters = {
		owner_id: searchParams.get('owner_id') || '',
		fund_id: searchParams.get('fund_id') || '',
		industry: searchParams.get('industry') || '',
	};

	const [filters, setFilters] = useState(initialFilters);

	// Local state for pipeline data to handle optimistic updates
	const [localPipelineData, setLocalPipelineData] = useState(pipelineData);

	// Update local state when data from loader changes
	useEffect(() => {
		setLocalPipelineData(pipelineData);
	}, [pipelineData]);

	// Handle the end of a drag operation
	const handleDragEnd = async (result) => {
		const { destination, source, draggableId: dealId } = result;

		// Dropped outside a droppable area or same position
		if (
			!destination ||
			(destination.droppableId === source.droppableId &&
				destination.index === source.index)
		) {
			return;
		}

		// Get new stage ID from destination
		const newStageId = destination.droppableId;
		const sourceStageId = source.droppableId;

		try {
			// Make a deep copy of the pipeline data for immutable update
			const updatedPipeline = JSON.parse(
				JSON.stringify(localPipelineData.pipeline)
			);

			// Find the source and destination stages
			const sourceStageIndex = updatedPipeline.findIndex(
				(s) => s.stage_id === sourceStageId
			);
			const destStageIndex = updatedPipeline.findIndex(
				(s) => s.stage_id === newStageId
			);

			// Extract the deal from source stage
			let movingDeal = null;
			if (sourceStageIndex !== -1) {
				const [removed] = updatedPipeline[sourceStageIndex].deals.splice(
					source.index,
					1
				);
				movingDeal = removed;
			}

			// Insert the deal into destination stage
			if (destStageIndex !== -1 && movingDeal) {
				// Update the deal's stage property
				movingDeal.stage = newStageId;
				movingDeal.stage_id = newStageId;

				// Add to destination
				updatedPipeline[destStageIndex].deals.splice(
					destination.index,
					0,
					movingDeal
				);

				// Update local state immediately for optimistic UI
				setLocalPipelineData({
					...localPipelineData,
					pipeline: updatedPipeline,
				});

				// Send update to API
				await authedApiFetch({
					endpoint: `/tenants/${tenantId}/deals/${dealId}`,
					method: 'PATCH',
					payload: {
						deal: {
							stage: newStageId,
						},
					},
				});
			}
		} catch (error) {
			console.error('Error updating deal stage:', error);
			// Revert to original data on error
			setLocalPipelineData(pipelineData);
			// TODO: Add error notification
		}
	};

	// Apply filters and reload data
	const handleFilterChange = (newFilters) => {
		setFilters(newFilters);

		// Create form data with the filter values
		const formData = new FormData();

		// Only add filters that have values
		if (newFilters.owner_id) formData.append('owner_id', newFilters.owner_id);
		if (newFilters.fund_id) formData.append('fund_id', newFilters.fund_id);
		if (newFilters.industry) formData.append('industry', newFilters.industry);

		// Submit the form to reload the page with the new filters in the URL
		submit(formData, {
			method: 'get',
			action: location.pathname,
			replace: true,
		});
	};

	const renderContent = () => {
		// If we have pipeline data, show the kanban board
		if (localPipelineData?.pipeline && localPipelineData.pipeline.length > 0) {
			return (
				<KanbanBoard
					pipeline={localPipelineData.pipeline}
					onDragEnd={handleDragEnd}
				/>
			);
		}

		// Otherwise show empty state
		return (
			<Paper
				sx={{
					p: 2,
					minHeight: 300,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: theme.palette.action.hover,
				}}
			>
				<Typography variant='subtitle1' color='text.secondary'>
					No pipeline data available
				</Typography>
			</Paper>
		);
	};

	return (
		<Box sx={{ p: 2 }}>
			<Paper
				sx={{ p: 2, mb: 2, backgroundColor: theme.palette.background.paper }}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant='h4'>Investment Pipeline</Typography>

					{/* Pipeline Filters Component - right aligned */}
					{pipelineData?.filters && (
						<PipelineFilters
							filterOptions={pipelineData.filters}
							currentFilters={filters}
							onFilterChange={handleFilterChange}
						/>
					)}
				</Box>
			</Paper>

			{renderContent()}
		</Box>
	);
};

export default PipelinePage;
